import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce/lib';
import { Queries } from '@shared/api';
import { useControlsState } from '@shared/components/filter/state/ControlsProvider';
import { useGroupContext } from '@shared/components/filter/state/GroupProvider';
import { useViewTemplateState } from '@shared/components/filter/state/ViewTemplateProvider';
import {
  FilterDateOperator,
  FilterDateSelection,
  FilterNumberSelection,
  FilterPersonSelection,
  FilterSelectionState,
  FilterTagSelection,
  FilterTextSelection,
  SortSelectionState,
} from '@shared/components/filter/types';
import { useCanViewCollectionView } from '@shared/data/billing/hooks/useCanViewCollectionView';
import { PRICING_LIMIT_HISTORY_DAYS } from '@shared/styles';
import { User } from '@shared/types/user';
import { usePaywallExperiment } from '@shared/util/featureFlags/launchDarkly/hooks/usePaywallExperiment';
import { useProfile } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { usePermission } from '@shared/util/permissions/hooks';
import { Entity } from '../entity/Entity';
import { CardEntity } from './CardEntity';
import { useLoadMore } from './hooks/useLoadMore';
import { CARDS_PER_GROUP_PAGE, CARDS_PER_PAGE, useUpdateList } from './hooks/useUpdateList';

export interface CardsEntityServiceProps {
  workflowTemplateId: string;
  viewTemplateId: string;
  workspaceId: number;
  pageSize?: number;
}

export interface CardsEntityService {
  entities: Entity[];
  entityVariables: MBAPI.ListWorkflowEntitiesQueryVariables;
  totalCardCount: number;
  isCardsLoading: boolean;
  isMoreCardsLoading: boolean;
  canUpdateCard: boolean;
  onSearch(keyword: string): void;
  onReorder(componentId: string, order: string): void;
  fetchMore(): void;
  refetch(): void;
  hasMoreCards: boolean;
  errors: Error[];
}

export function useCardService({
  workflowTemplateId,
  viewTemplateId,
  workspaceId,
  pageSize,
}: CardsEntityServiceProps): CardsEntityService {
  const {
    sorts: selectedSortOptions,
    filters: selectedFilterOptions,
    availableSorts,
    search,
  } = useControlsState();
  const group = useGroupContext();
  const isGroup = !isNil(group);
  const canUpdateCard = usePermission({
    action: 'update',
    subject: 'Card',
    channelId: workspaceId,
  }).enabled;

  const { viewTemplate } = useViewTemplateState();
  const { isBannerGroup } = usePaywallExperiment();
  const isPaid = useCanViewCollectionView();

  const sortByComponents = useMemo(() => {
    return Array.from(selectedSortOptions.values()).map(getSortComponent);
  }, [selectedSortOptions]);

  const filterByComponents = useMemo(() => {
    const selected = new Map(selectedFilterOptions);
    if (isGroup) {
      Array.from(group.entries()).forEach(([id, filter]) => {
        selected.set(id, filter);
      });
    }

    const converted = Array.from(selected.values())
      .map(v => getFilterComponent({ ...v, prepareForMutation: false }))
      .filter(notNil);
    if (!isPaid && isBannerGroup) {
      converted.push({
        componentId: 'createdAt',
        value: {
          dateRange: {
            after: moment()
              .subtract(PRICING_LIMIT_HISTORY_DAYS, 'days')
              .startOf('day')
              .toISOString(),
            before: null,
          },
          dateIs: null,
          dateTokenIs: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
    }
    return converted;
  }, [selectedFilterOptions, group, isGroup, isPaid, isBannerGroup]);

  const entityVariables: MBAPI.ListWorkflowEntitiesQueryVariables = useMemo(() => {
    const sortByCreatedAt: MBAPI.SortByComponent = {
      componentId: 'createdAt',
      direction: 'DESC',
    };

    const hasCreatedAtSort = availableSorts.some(item => item.id === 'createdAt');
    const customSortingEnabled = !!viewTemplate?.customSortingEnabled;
    const shouldUseCreatedAtAsDefaultSort = hasCreatedAtSort && !customSortingEnabled;
    const defaultSort = shouldUseCreatedAtAsDefaultSort ? [sortByCreatedAt] : [];

    const defaultPageSize = isGroup ? CARDS_PER_GROUP_PAGE : CARDS_PER_PAGE;

    return {
      input: {
        workflowTemplateId,
        viewTemplateId,
        limit: pageSize ?? defaultPageSize,
        offset: 0,
        sortByComponents: !isEmpty(sortByComponents) ? sortByComponents : defaultSort,
        filterByComponents,
        omniSearchQuery: search,
      },
    };
  }, [
    availableSorts,
    filterByComponents,
    isGroup,
    pageSize,
    search,
    sortByComponents,
    workflowTemplateId,
    viewTemplateId,
    viewTemplate?.customSortingEnabled,
  ]);

  const {
    data: listEntities,
    loading: isEntitiesLoading,
    fetchMore: entityFetchMore,
    refetch,
  } = useQuery<MBAPI.ListWorkflowEntitiesQuery, MBAPI.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables: entityVariables,
      skip: isNil(workflowTemplateId),
      fetchPolicy: 'cache-and-network',
    },
  );

  const { isLoadingMore, loadMore } = useLoadMore(entityFetchMore, listEntities, entityVariables);
  const hasMoreCards = listEntities?.list.hasMore ?? false;

  const [fetchMoreCards] = useDebouncedCallback(
    useCallback(() => {
      if (isLoadingMore || !hasMoreCards) {
        return;
      }

      loadMore();
    }, [isLoadingMore, loadMore, hasMoreCards]),
    100,
  );

  useUpdateList(entityVariables);
  const { profile } = useProfile();

  const entities = useMemo(
    () => getEntities({ listEntities, workflowTemplateId, viewTemplate, currentUser: profile }),
    [listEntities, workflowTemplateId, viewTemplate, profile],
  );

  const totalCardCount = listEntities?.list.totalResults ?? 0;

  return useMemo(() => {
    return {
      entities,
      entityVariables,
      totalCardCount,
      hasMoreCards,
      fetchMore: fetchMoreCards,
      refetch,
      canUpdateCard,
      isCardsLoading: isEntitiesLoading,
      isMoreCardsLoading: isLoadingMore,
      onSearch: _ => {},
      onReorder: (_, __) => {},
      errors: [],
    };
  }, [
    entities,
    entityVariables,
    totalCardCount,
    fetchMoreCards,
    canUpdateCard,
    hasMoreCards,
    isEntitiesLoading,
    isLoadingMore,
    refetch,
  ]);
}

export function getFilterComponent({
  filter,
  selection,
  prepareForMutation = true,
  negate = false,
}: FilterSelectionState & { prepareForMutation?: boolean }): MBAPI.FilterByComponent | null {
  switch (filter?.type) {
    case 'TAG':
    case 'RELATED_CARD': {
      const filterValues = (selection as FilterTagSelection[])
        .filter(s => s.value !== 'empty')
        .filter(s => !isNil(s.value))
        .map(s => s.value);
      let containsString: MBAPI.FilterByComponent_value_containsString | null = null;
      if (!isEmpty(filterValues)) {
        containsString = {
          values: filterValues,
          negate,
        };
      }
      const emptyValue = !isNil((selection as FilterTagSelection[]).find(s => s.value === 'empty'));
      return {
        componentId: filter.id,
        value: {
          containsString,
          isEmpty: emptyValue
            ? {
                value: emptyValue,
              }
            : null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      };
    }
    case 'PERSON': {
      const emptyValue = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'empty'),
      );
      const currentUser = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'currentUser'),
      );
      const filterValues =
        !emptyValue && !currentUser
          ? (selection as FilterPersonSelection[])
              .filter(s => s.value !== 'empty' && s.value !== 'currentUser')
              .map(s => Number(s.value))
          : null;
      let containsNumber: MBAPI.FilterByComponent_value_containsNumber | null = null;
      if (!isNil(filterValues) && !isEmpty(filterValues)) {
        containsNumber = {
          values: filterValues,
          negate,
        };
      }
      return {
        componentId: filter.id,
        value: {
          containsNumber,
          isEmpty: emptyValue
            ? {
                value: emptyValue,
              }
            : null,
          stringWildcard: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: currentUser ? { value: currentUser } : null,
        },
      };
    }
    case 'TEXT': {
      const isSelectionEmpty = (selection as FilterTextSelection)?.operator === 'empty';
      return {
        componentId: filter.id,
        value: {
          stringWildcard: isSelectionEmpty
            ? null
            : {
                value: (selection as FilterTextSelection).value ?? '',
              },
          isEmpty: isSelectionEmpty ? { value: true } : null,
          containsNumber: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      };
    }
    case 'DATE': {
      const dateSelection = selection as FilterDateSelection;
      const isToday = dateSelection?.operator === 'today';
      let dateRange: MBAPI.FilterByComponent_value_dateRange | null = null;

      if (operatorsThatNeedDates.includes(dateSelection?.operator)) {
        dateRange = getFilterDateRange(dateSelection);
      }

      const todayDateProps = getTodayDateFilters({
        hasTodayToken: isToday,
        convertToDateRange: !prepareForMutation,
      });

      const duration = !isNil(dateSelection?.duration) ? Math.abs(dateSelection.duration) : 0;
      const relativeDateProps = getRelativeDateFilters({
        isRelative: relativeOperators.includes(dateSelection?.operator),
        comparisonOperator: dateSelection?.comparisonOperator,
        duration: dateSelection?.operator === 'daysBeforeToday' ? -duration : duration,
        convertToDateRange: !prepareForMutation,
      });

      return {
        componentId: filter.id,
        value: {
          dateRange,
          ...todayDateProps,
          ...relativeDateProps,
          isEmpty: dateSelection?.operator === 'empty' ? { value: true } : null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      };
    }
    case 'NUMBER': {
      const operator = (selection as FilterNumberSelection).operator;
      return {
        componentId: filter.id,
        value: {
          numberIs:
            operator !== 'empty'
              ? {
                  operator: operator,
                  value: (selection as FilterNumberSelection).value,
                }
              : null,
          isEmpty: operator === 'empty' ? { value: true } : null,
          stringWildcard: null,
          containsNumber: null,
          containsString: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          containsCurrentUser: null,
        },
      };
    }
    default: {
      return null;
    }
  }
}

const operatorsThatNeedDates: FilterDateOperator[] = ['exactly', 'between', 'startsOn', 'endsOn'];
const operatorsThatNeedStartDate: FilterDateOperator[] = ['exactly', 'between', 'startsOn'];
const operatorsThatNeedEndDate: FilterDateOperator[] = ['between', 'endsOn'];
const relativeOperators: FilterDateOperator[] = ['daysAfterToday', 'daysBeforeToday'];

function getFilterDateRange(
  dateSelection: FilterDateSelection,
): MBAPI.FilterByComponent_value_dateRange | null {
  if (operatorsThatNeedDates.includes(dateSelection?.operator)) {
    return {
      after: operatorsThatNeedStartDate.includes(dateSelection.operator)
        ? (dateSelection.after as MBAPI.Scalar.DateTime)
        : null,
      before: operatorsThatNeedEndDate.includes(dateSelection.operator)
        ? dateSelection.before ?? null
        : dateSelection.operator === 'exactly'
        ? (moment(dateSelection.after).endOf('day').toISOString() as MBAPI.Scalar.DateTime)
        : null,
    };
  } else {
    return null;
  }
}

export function getTodayDateFilters({
  hasTodayToken,
  convertToDateRange,
}: {
  hasTodayToken: boolean;
  convertToDateRange: boolean;
}): {
  dateRange?: MBAPI.FilterByComponent_value_dateRange;
  dateTokenIs: MBAPI.FilterByComponent_value_dateTokenIs | null;
} {
  if (hasTodayToken && convertToDateRange) {
    return {
      dateRange: {
        after: moment().startOf('day').toISOString(),
        before: moment().endOf('day').toISOString(),
      },
      dateTokenIs: null,
    };
  }
  if (hasTodayToken) {
    return {
      dateTokenIs: { value: 'TODAY' },
    };
  }
  return {
    dateTokenIs: null,
  };
}

export function getRelativeDateFilters({
  isRelative,
  comparisonOperator,
  duration,
  convertToDateRange,
}: {
  isRelative: boolean;
  comparisonOperator?: MBAPI.ComparisonOperator;
  duration?: number;
  convertToDateRange: boolean;
}): {
  dateRange?: MBAPI.FilterByComponent_value_dateRange;
  dateIs: MBAPI.FilterByComponent_value_dateIs | null;
} {
  const isValidRelativeDate = isRelative && !isNil(comparisonOperator) && !isNil(duration);

  /* Notes
  Specified day is (+/-) duration days from today

  GT/GTE - after
    - !E is exclusive. start of day after specified day.
    - E is inclusive. start of specified day.
  LT/LTE - before
    - !E is exclusive. end of day before specified day.
    - E is inclusive. end of specified day
  */
  if (isValidRelativeDate && convertToDateRange) {
    const specifiedDay = moment().add(duration, 'days');
    let after: string | null = null;
    let before: string | null = null;
    if (comparisonOperator === 'GT') {
      after = specifiedDay.add(1, 'day').startOf('day').toISOString();
    }
    if (comparisonOperator === 'GTE') {
      after = specifiedDay.startOf('day').toISOString();
    }
    if (comparisonOperator === 'LT') {
      before = specifiedDay.subtract(1, 'day').endOf('day').toISOString();
    }
    if (comparisonOperator === 'LTE') {
      before = specifiedDay.endOf('day').toISOString();
    }

    return {
      dateRange: {
        after,
        before,
      },
      dateIs: null,
    };
  }

  if (isValidRelativeDate) {
    return {
      dateIs: {
        operator: comparisonOperator,
        relativeDate: {
          duration,
          timeUnit: 'DAYS',
        },
      },
    };
  }

  return {
    dateIs: null,
  };
}

export function getSortComponent({ sort, direction }: SortSelectionState): MBAPI.SortByComponent {
  return {
    componentId: sort.id,
    direction,
  };
}

interface GetEntitiesProps {
  listEntities: MBAPI.ListWorkflowEntitiesQuery | undefined;
  workflowTemplateId: string;
  viewTemplate: MBAPI.ViewTemplate | undefined;
  currentUser?: User;
}

function getEntities({
  listEntities,
  workflowTemplateId,
  viewTemplate,
  currentUser,
}: GetEntitiesProps): Entity[] {
  const entityList = listEntities?.list.entities;
  const refs = listEntities?.list.refs;
  if (!entityList || !refs || !viewTemplate) {
    return [];
  }

  return entityList
    .filter(isEntity)
    .map(entity => new CardEntity({ entity, workflowTemplateId, refs, viewTemplate, currentUser }));
}

export function isEntity(
  entity: MBAPI.ListWorkflowEntitiesQuery_list_entities | null,
): entity is MBAPI.ListWorkflowEntitiesQuery_list_entities {
  return !isNil(entity);
}
